import { useEffect, useState } from 'react';
import { Alert, Box, MenuItem } from '@mui/material';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
  CustomAutoComplete,
  CustomTextField,
} from '../../../common/components';
import { INITIAL_WAREHOUSE_STATE } from '../constants/warehousesConstants';
import { getWarehouseApiErrorMessage, validateWarehouse } from '../utils/warehousesUtils';
import { guardDialogClose } from '../../utils/actionLockUtils';

const STATUS_OPTIONS = [
  { value: true, label: 'Activo' },
  { value: false, label: 'Inactivo' },
];

const WarehouseDialog = ({
  open,
  onClose,
  editMode,
  selectedWarehouse,
  branches = [],
  onCreate,
  onUpdate,
  isSubmitting,
}) => {
  const [formData, setFormData] = useState(INITIAL_WAREHOUSE_STATE);
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (open) {
      if (editMode && selectedWarehouse) {
        setFormData({
          uuid: selectedWarehouse.uuid,
          code: selectedWarehouse.code || '',
          name: selectedWarehouse.name || '',
          uuid_branch: selectedWarehouse.uuid_branch || '',
          address: selectedWarehouse.address || '',
          is_active: selectedWarehouse.is_active ?? true,
        });
      } else {
        setFormData(INITIAL_WAREHOUSE_STATE);
      }
      setErrors({});
      setErrorMessage('');
    }
  }, [open, editMode, selectedWarehouse]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }

    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const handleStatusChange = (event) => {
    const value = event.target.value === 'true' || event.target.value === true;
    setFormData((prev) => ({
      ...prev,
      is_active: value,
    }));

    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const handleSubmit = async () => {
    const validationErrors = validateWarehouse(formData);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const payload = {
      code: formData.code.trim(),
      name: formData.name.trim(),
      uuid_branch: formData.uuid_branch || null,
      address: formData.address ? formData.address.trim() : null,
    };

    try {
      setErrorMessage('');

      if (editMode && formData.uuid) {
        await onUpdate(formData.uuid, {
          ...payload,
          is_active: formData.is_active,
        });
      } else {
        await onCreate(payload);
      }
      onClose();
    } catch (error) {
      console.error('Error al guardar el depósito:', error);
      setErrorMessage(getWarehouseApiErrorMessage(error));
    }
  };

  return (
    <DialogContainer open={open} onClose={guardDialogClose(isSubmitting, onClose)} size="small">
      <DialogTitle>
        {editMode ? 'Editar depósito' : 'Nuevo depósito'}
      </DialogTitle>

      {errorMessage && (
        <Box sx={{ px: 3, pt: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      <DialogBody sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <CustomTextField
          label="Código"
          name="code"
          value={formData.code}
          onChange={handleChange}
          required
          error={Boolean(errors.code)}
          inputProps={{ maxLength: 50 }}
        />

        <CustomTextField
          label="Nombre"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          error={Boolean(errors.name)}
          inputProps={{ maxLength: 150 }}
        />

        <CustomAutoComplete
          label="Sucursal"
          name="uuid_branch"
          placeholder="Seleccioná una sucursal (opcional)"
          value={formData.uuid_branch}
          onChange={handleChange}
        >
          <MenuItem value="">Sin sucursal</MenuItem>
          {branches.map((branch) => (
            <MenuItem key={branch.uuid} value={branch.uuid}>
              {branch.description}
            </MenuItem>
          ))}
        </CustomAutoComplete>

        <CustomTextField
          label="Dirección"
          name="address"
          value={formData.address}
          onChange={handleChange}
          error={Boolean(errors.address)}
          inputProps={{ maxLength: 255 }}
        />

        {editMode && (
          <CustomAutoComplete
            label="Estado"
            name="is_active"
            value={formData.is_active}
            onChange={handleStatusChange}
          >
            {STATUS_OPTIONS.map((option) => (
              <MenuItem key={String(option.value)} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </CustomAutoComplete>
        )}
      </DialogBody>

      <DialogActions
        handleClose={onClose}
        handleSave={handleSubmit}
        saveText={editMode ? 'Actualizar' : 'Crear'}
        isSaving={isSubmitting}
      />
    </DialogContainer>
  );
};

export default WarehouseDialog;
